import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useSubscriptionActions } from '../../../store/subscription/hooks';
import event from '../../../utils/analytics/event';
import BodyText from '../../elements/BodyText';
import Title from '../../elements/Title';
import { Container, LoadingSpinner } from './EmailConfirmation.styles';
/**
 * A component that completely handles confirming a user when it renders. In
 * order for this component to work correctly, the following things must be
 * true:
 *
 * - You have integrated the Pantheon `subscription` model into your app
 * - The component is rendered on a react router route that has the user id
 *   and confirmation token as parameters in the url. These must be called
 *   `id` and `token` (example valid path: `/confirm/:id/:token`)
 *
 * This is NOT themable, but uses the `Title` and `BodyText` components, which
 * can be themed.
 *
 * ```js
 * import { EmailConfirmation } from '@minerva-analytics/pantheon';
 * ```
 *
 * By default, it logs 2 possible piwik events:
 *
 * - `event(['auth', 'confirm-email-success', <id>]);`
 * - `event(['auth', 'confirm-email-failure', <id>]);`
 *
 * If an object is passed for the piwikEvents param, then 2 custom piwik events will be logged instead of the default options.
 *
 * `piwikEvents` must be passed in as such: `{success:[], failure:[]}`
 */

var EmailConfirmation = function EmailConfirmation(props) {
  var _useSubscriptionActio = useSubscriptionActions(),
      confirmSubscriber = _useSubscriptionActio.confirmSubscriber;

  var _useParams = useParams(),
      id = _useParams.id,
      token = _useParams.token;

  var status = useSelector(function (state) {
    if (!state.subscription) {
      throw new Error('<EmailConfirmation> was used without the Pantheon subscription model set up');
    }

    return state.subscription.confirmSubscriberStatus;
  });
  useEffect(function () {
    if (status.loaded && !status.failed) {
      event(props.piwikEvents && props.piwikEvents.success ? props.piwikEvents.success : ['auth', 'confirm-email-success', "" + id]);
    } else if (status.failed) {
      event(props.piwikEvents && props.piwikEvents.failure ? props.piwikEvents.failure : ['auth', 'confirm-email-failure', "" + id]);
    }
  }, [status.loaded, status.failed]);
  useEffect(function () {
    var confirm = props.confirmSubscriber || confirmSubscriber;
    confirm(id, token, props.confirmRequestConfig);
  }, []); // Confirmation failed

  if (status.failed) {
    var confirmed = status.error.response && status.error.response.data.detail === "Already confirmed";
    return /*#__PURE__*/React.createElement(Container, {
      className: props.className || ''
    }, /*#__PURE__*/React.createElement(Title, null, confirmed ? props.successTitle : props.failureTitle), /*#__PURE__*/React.createElement(BodyText, null, confirmed ? props.successText : props.failureText), confirmed ? props.children : null);
  } // Confirmation pending


  if (!status.loaded || status.loading) {
    return /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(LoadingSpinner, null));
  } // Confirmation successful


  return /*#__PURE__*/React.createElement(Container, {
    className: props.className || ''
  }, /*#__PURE__*/React.createElement(Title, null, props.successTitle), /*#__PURE__*/React.createElement(BodyText, null, props.successText), props.children);
};

EmailConfirmation.propTypes = {
  /** The text shown below the title when confirmation is successful */
  successText: PropTypes.string,

  /** The title shown at the top of the page when confirmation is successful */
  successTitle: PropTypes.string,

  /** The text shown below the title when confirmation is unsuccessful */
  failureText: PropTypes.string,

  /** The title shown at the top of the page when confirmation is unsuccessful */
  failureTitle: PropTypes.string,

  /** A custom function to call instead of the default confirmSubscriber */
  confirmSubscriber: PropTypes.func,

  /**
   * A custom classname to apply to the container. Usually provided automatically
   * by styled-components by doing `styled(EmailConfirmation)`
   */
  className: PropTypes.string,

  /** A custom request config to pass to `confirmSubscriber` */
  confirmRequestConfig: PropTypes.object,

  /** Optional object consiting of 2 custom events: success & failure. Object keys must be `success` and `failure` with values as arrays in the form of [category, action, name, value], though name and value are optional. */
  piwikEvents: PropTypes.object,
  children: PropTypes.node
};
EmailConfirmation.defaultProps = {
  successText: "We're looking forward to sharing the latest news with you.",
  successTitle: 'Thanks for confirming!',
  failureText: 'Please try again later.',
  failureTitle: 'Something went wrong'
};
export default EmailConfirmation;